/*
 * Copyright (C) 2013-2026 Neckar IT GmbH, Mössingen, Germany
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Linking this library statically or dynamically with other modules is
 * making a combined work based on this library. Thus, the terms and
 * conditions of the GNU General Public License cover the whole combination.
 *
 * As a special exception, the copyright holders of this library give you
 * permission to link this library with independent modules, regardless of
 * the license terms of these independent modules, and to copy and distribute
 * the resulting combined work under terms of your choice, provided that every
 * copy of the combined work is accompanied by a complete copy of the source
 * code of this library.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 */
package it.neckar.open.formatting

import it.neckar.datetime.minimal.TimeConstants
import it.neckar.open.i18n.I18nConfiguration
import it.neckar.open.kotlin.lang.WhitespaceConfig
import it.neckar.open.kotlin.lang.toIntFloor
import it.neckar.open.time.formatHumanized
import it.neckar.open.time.nowMillis
import it.neckar.open.unit.si.ms
import it.neckar.time.Millis
import kotlin.time.Duration.Companion.milliseconds

/**
 * Formats a date and/or time
 */
interface DateTimeFormat {
  /**
   * Formats a date to a string
   */
  fun format(@ms timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String

  /**
   * Format the provided timestamp. If the timestamp is null, the [fallback] is returned.
   */
  fun formatNullable(@ms timestamp: Double?, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking, fallback: String): String {
    if (timestamp == null) {
      return fallback
    }

    return format(timestamp, i18nConfiguration, whitespaceConfig)
  }
}

/**
 * Formats a date and time in accordance to the ISO format 8601
 */
val dateTimeFormatIso8601: CachedDateTimeFormat = DateTimeFormatIso8601().cached()

/**
 * Formats only the date according to ISO 8601
 */
val dateFormatIso8601: CachedDateTimeFormat = DateFormatIso8601().cached()

/**
 * Formats only the (local!) time according to ISO 8601.
 */
val timeFormatIso8601: CachedDateTimeFormat = TimeFormatIso8601().cached()

/**
 * Formats a date as a UTC date (locale independent)
 */
val dateTimeFormatUTC: CachedDateTimeFormat = DateTimeFormatUTC().cached()

/**
 * A formatted date (time only)
 */
val timeFormat: CachedDateTimeFormat = TimeFormat().cached()

/**
 * A formatted time including milliseconds
 */
val timeFormatWithMillis: CachedDateTimeFormat = TimeFormatWithMillis().cached()

/**
 * A format for a date (without time)
 */
val dateFormat: CachedDateTimeFormat = DateFormat().cached()

/**
 * Prints the year and months
 */
val yearMonthFormat: CachedDateTimeFormat = YearMonthFormat().cached()

/**
 * Prints the year
 */
val yearFormat: CachedDateTimeFormat = YearFormat().cached()

/**
 * Formats the second and millisecond
 */
val secondMillisFormat: CachedDateTimeFormat = SecondMillisFormat().cached()

/**
 * A format for a date and a time
 */
val dateTimeFormat: CachedDateTimeFormat = DefaultDateTimeFormat().cached()

/**
 * A format for a date and a time (short date format)
 */
val dateTimeFormatShort: CachedDateTimeFormat = DateTimeFormatShort().cached()

/**
 * A format for a date and a time with millis
 */
val dateTimeFormatWithMillis: CachedDateTimeFormat = DateTimeFormatWithMillis().cached()

/**
 * A format for a date and a time with millis (short date format)
 */
val dateTimeFormatShortWithMillis: CachedDateTimeFormat = DateTimeFormatShortWithMillis().cached()

/**
 * Formats a date in a nice, human-readable way (e.g "gestern", "vor 5 Minuten")
 */
fun humanizedDate(@ms timestamp: Double, i18nConfiguration: I18nConfiguration, now: @ms Double = nowMillis(), whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
  val timeSince = (now - timestamp).milliseconds
  return timeSince.formatHumanized() ?: "seit${whitespaceConfig.space}${dateFormat.format(timestamp, i18nConfiguration, whitespaceConfig)}"
}

/**
 * Formats a date-time in accordance to the ISO format 8601 (https://en.wikipedia.org/wiki/ISO_8601)
 *
 * Example: `2001-09-09T01:46:40.000Z`
 *
 * This formatter always returns "Z" (UTC) as time zone
 */
expect class DateTimeFormatIso8601() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String

  companion object {
    /**
     * Parses the date-time string to a timestamp
     */
    fun parse(formattedIsoString: String): @ms Double
  }
}

expect class DateFormatIso8601() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

expect class TimeFormatIso8601() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * Formats a date as a UTC date (locale independent)
 */
expect class DateTimeFormatUTC() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String

  companion object {
    /**
     * Parses the UTC string to a timestamp
     */
    fun parse(formattedUtc: String): @ms Double
  }
}

/**
 * A formatted date (time only)
 */
expect class TimeFormat() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * A formatted time including milliseconds
 */
expect class TimeFormatWithMillis() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * A format for a date (without time)
 */
expect class DateFormat() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * A format that formats a date - but only prints the month and year
 */
expect class YearMonthFormat() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * A format that formats a date - but only prints the year
 */
expect class YearFormat() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * Formats a time stamp as second with millis
 */
expect class SecondMillisFormat() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * A format for a date and a time
 */
expect class DefaultDateTimeFormat() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * A format for a date and a time (short date format)
 */
expect class DateTimeFormatShort() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * A format for a date and a time with millis
 */
expect class DateTimeFormatWithMillis() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}

/**
 * A format for a date and a time with millis (short date format)
 */
expect class DateTimeFormatShortWithMillis() : DateTimeFormat {
  override fun format(timestamp: Double, i18nConfiguration: I18nConfiguration, whitespaceConfig: WhitespaceConfig): String
}


fun @ms Millis.formatUtc(whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
  return this.millis.formatUtc(whitespaceConfig)
}

/**
 * Formats millis as UTC. Uses [dateTimeFormatUTC] - with cache.
 *
 * Use [parseUtc] to parse the date again
 */
fun @ms Double.formatUtc(whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
  if (this.isNaN()) {
    return "NaN"
  }

  if (this.isInfinite()) {
    return "∞"
  }

  return dateTimeFormatUTC.format(this, I18nConfiguration.GermanyUTC, whitespaceConfig)
}

/**
 * Parses a UTC date - reverse for [formatUtc
 */
fun parseUtc(formattedUtc: String): @ms Double {
  if (formattedUtc == "NaN") {
    return Double.NaN
  }

  if (formattedUtc == "∞") {
    return Double.POSITIVE_INFINITY
  }

  return DateTimeFormatUTC.parse(formattedUtc)
}


/**
 * Formats this (interpreted as milliseconds) as human-readable duration
 */
fun @ms Double.formatAsDuration(whitespaceConfig: WhitespaceConfig = WhitespaceConfig.NonBreaking): String {
  if (this.isNaN()) {
    return "NaN"
  }

  if (this.isInfinite()) {
    return "∞"
  }


  var days: Int = 0
  var hours: Int = 0
  var minutes: Int = 0
  var seconds: Int = 0
  var milliseconds: Double = this

  days = (milliseconds / TimeConstants.MillisPerDay).toIntFloor()
  milliseconds = milliseconds - days * TimeConstants.MillisPerDay

  hours = (milliseconds / TimeConstants.MillisPerHour).toIntFloor()
  milliseconds = milliseconds - hours * TimeConstants.MillisPerHour

  minutes = (milliseconds / TimeConstants.MillisPerMinute).toIntFloor()
  milliseconds = milliseconds - minutes * TimeConstants.MillisPerMinute

  seconds = (milliseconds / TimeConstants.MillisPerSecond).toIntFloor()
  milliseconds = milliseconds - seconds * TimeConstants.MillisPerSecond

  return buildString {
    var addedSomething = false

    if (addedSomething || days != 0) {
      append("${intFormat.format(days.toDouble())}${whitespaceConfig.smallSpace}days${whitespaceConfig.space}")
      addedSomething = true
    }
    if (addedSomething || hours != 0) {
      append("${hours}${whitespaceConfig.smallSpace}h${whitespaceConfig.space}")
      addedSomething = true
    }
    if (addedSomething || minutes != 0) {
      append("${minutes}${whitespaceConfig.smallSpace}min${whitespaceConfig.space}")
      addedSomething = true
    }
    if (addedSomething || seconds != 0) {
      append("${seconds}${whitespaceConfig.smallSpace}s${whitespaceConfig.space}")
      addedSomething = true
    }

    append("${milliseconds}${whitespaceConfig.smallSpace}ms")
  }
}
